import React, { useState, useRef, useEffect } from 'react';

import { Button, Typography, Chip, FormControl, MenuItem, Select, Tooltip } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CommentOutlinedIcon from '@material-ui/icons/CommentOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import type {
  ActionLibraryInstanceStep,
  ActionLibraryInstanceStepStatus,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

import StepCommentsDialog from '../StepCommentsDialog/StepCommentsDialog';
import StepStatusLogs from '../StepStatusLogs/StepStatusLogs';

import useStyles from './ActionLibraryInstanceDetailsSteps.styles';

const STEP_STATUSES: ActionLibraryInstanceStepStatus[] = ['not_started', 'in_progress', 'completed', 'not_applicable'];

type StepItemProps = {
  step: ActionLibraryInstanceStep;
  index: number;
  readOnly?: boolean;
  onStepStatusChange?: (stepId: number, status: ActionLibraryInstanceStepStatus) => void;
};

const StepItem: React.FC<StepItemProps> = ({ step, index, readOnly, onStepStatusChange }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [actionLogsOpen, setActionLogsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: actionLogsCount } = useQuery(['ACTION_INSTANCE_STEP_STATUS_LOGS_COUNT', step.id], () =>
    actionInstanceStepApi.getStatusLogs(step.id, { limit: 1 }).then(res => res.data.count),
  );

  useEffect(() => {
    const element = contentRef.current;
    if (element) {
      const checkOverflow = () => {
        if (!expanded) {
          setIsOverflowing(element.scrollHeight > element.clientHeight);
        }
      };

      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }
    return () => {};
  }, [expanded, step.instruction]);

  const currentStatus = (step.status || (step.isCompleted ? 'completed' : 'not_started')) as ActionLibraryInstanceStepStatus;

  const getStatusDisplay = () => {
    if (currentStatus === 'completed') {
      return {
        Icon: CheckCircleIcon,
        color: styles.completedColor,
        label: step.statusDisplay || t(action_library_messages.instance_actions.step_status_done),
      };
    }
    if (currentStatus === 'in_progress') {
      return {
        Icon: RemoveCircleOutlineIcon,
        color: styles.inProgressColor,
        label: step.statusDisplay || t(action_library_messages.instance_actions.step_status_in_progress),
      };
    }
    if (currentStatus === 'not_applicable') {
      return {
        Icon: CancelIcon,
        color: styles.notApplicableColor,
        label: step.statusDisplay || t(action_library_messages.instance_actions.step_status_not_applicable),
      };
    }
    return {
      Icon: CancelIcon,
      color: styles.notStartedColor,
      label: step.statusDisplay || t(action_library_messages.instance_actions.step_status_not_started),
    };
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.Icon;

  const getStepCardStatusClass = () => {
    if (currentStatus === 'completed') return styles.stepCardCompleted;
    if (currentStatus === 'in_progress') return styles.stepCardInProgress;
    if (currentStatus === 'not_applicable') return styles.stepCardNotApplicable;
    return styles.stepCardNotStarted;
  };
  const stepCardStatusClass = getStepCardStatusClass();

  const getStatusLabel = (status: ActionLibraryInstanceStepStatus) => {
    switch (status) {
      case 'completed':
        return t(action_library_messages.instance_actions.step_status_done);
      case 'in_progress':
        return t(action_library_messages.instance_actions.step_status_in_progress);
      case 'not_applicable':
        return t(action_library_messages.instance_actions.step_status_not_applicable);
      default:
        return t(action_library_messages.instance_actions.step_status_not_started);
    }
  };

  const canMarkNotApplicable = !step.mandatory;

  return (
    <div className={`${styles.stepCard} ${stepCardStatusClass}`} id={`step-${step.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography style={{ display: 'flex', alignItems: 'center', gap: 8 }} variant='h6'>
          {index + 1}. {step.title}
          {step.mandatory && <Chip className={styles.mandatoryChip} label='Mandatory' size='small' variant='outlined' />}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tooltip title={t(action_library_messages.instance_actions.step_comments)}>
            <Button
              aria-label={t(action_library_messages.instance_actions.step_comments)}
              onClick={() => setCommentsOpen(true)}
              size='small'
              startIcon={<CommentOutlinedIcon />}
            >
              {t(action_library_messages.instance_actions.step_comments)}
            </Button>
          </Tooltip>
          {readOnly || !onStepStatusChange ? (
            <>
              <StatusIcon className={statusDisplay.color} style={{ fontSize: '1.2rem' }} />
              <Typography className={statusDisplay.color} variant='body2'>
                {statusDisplay.label}
              </Typography>
            </>
          ) : (
            <FormControl className={styles.statusSelect} size='small' variant='outlined'>
              <Select onChange={e => onStepStatusChange(step.id, e.target.value as ActionLibraryInstanceStepStatus)} value={currentStatus}>
                {STEP_STATUSES.map(s => {
                  if (s === 'not_applicable' && !canMarkNotApplicable) {
                    return (
                      <Tooltip key={s} title={t(action_library_messages.instance_actions.mark_as_na_tooltip_mandatory)}>
                        <span>
                          <MenuItem disabled value={s}>
                            {getStatusLabel(s)}
                          </MenuItem>
                        </span>
                      </Tooltip>
                    );
                  }
                  return (
                    <MenuItem key={s} value={s}>
                      {getStatusLabel(s)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </div>
      </div>
      <Typography component='div' style={{ marginBottom: 8 }} variant='body2'>
        <div ref={contentRef} className={!expanded ? styles.clampedText : ''}>
          <RichTextContent content={step.instruction} />
        </div>
        {(isOverflowing || expanded) && (
          <div className={styles.readMoreContainer}>
            <Button
              className={styles.readMoreButton}
              onClick={() => setExpanded(!expanded)}
              size='small'
              startIcon={expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
            >
              {expanded ? t(general_messages.hide) : t(general_messages.show_more)}
            </Button>
          </div>
        )}
      </Typography>
      {step.blockReason && (
        <Typography color='error' style={{ marginTop: 8 }} variant='body2'>
          <strong>Block Reason:</strong> {step.blockReason}
        </Typography>
      )}
      {step.completionComment && (
        <Typography color='textSecondary' style={{ marginTop: 8 }} variant='body2'>
          <strong>Completion Comment:</strong> {step.completionComment}
        </Typography>
      )}
      <div className={styles.readMoreContainer}>
        <Button
          className={styles.readMoreButton}
          onClick={() => setActionLogsOpen(!actionLogsOpen)}
          size='small'
          startIcon={actionLogsOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
        >
          {actionLogsOpen
            ? t(action_library_messages.instance.detail.hide_action_logs)
            : t(action_library_messages.instance.detail.show_action_logs)}{' '}
          ({actionLogsCount ?? 0})
        </Button>
      </div>
      {actionLogsOpen && (
        <div style={{ marginTop: 8 }}>
          <StepStatusLogs stepId={step.id} />
        </div>
      )}
      <StepCommentsDialog onClose={() => setCommentsOpen(false)} open={commentsOpen} stepId={step.id} stepTitle={step.title} />
    </div>
  );
};

type Props = {
  steps?: ActionLibraryInstanceStep[];
  readOnly?: boolean;
  onStepStatusChange?: (stepId: number, status: ActionLibraryInstanceStepStatus) => void;
};

const ActionLibraryInstanceDetailsSteps: React.FC<Props> = ({ steps, readOnly, onStepStatusChange }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <>
      {[...steps]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((step, index) => (
          <StepItem key={step.id} index={index} onStepStatusChange={onStepStatusChange} readOnly={readOnly} step={step} />
        ))}
    </>
  );
};

export default ActionLibraryInstanceDetailsSteps;
